import { ArrowRight, Check, Code2, Flame, Send, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Countdown } from "@/components/ui/Countdown";
import { SlotsMeter } from "@/components/ui/SlotsMeter";
import { Reveal } from "@/components/ui/Reveal";

const Badge = ({ plan }) => {
  if (!plan.badge) return null;
  if (plan.badge_style === "solid") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
        style={{ background: "var(--gm-red)" }}
      >
        <Star className="h-3 w-3" />
        {plan.badge}
      </span>
    );
  }
  if (plan.badge_style === "ring") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
        style={{
          border: "1px solid rgba(224,31,38,0.6)",
          background: "rgba(224,31,38,0.1)",
        }}
      >
        <Flame className="h-3 w-3" style={{ color: "#FF6B70" }} />
        {plan.badge}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
      style={{
        border: "1px solid rgba(255,255,255,0.18)",
        color: "var(--gm-muted)",
      }}
    >
      {plan.badge}
    </span>
  );
};

const FeatureList = ({ features, numbered }) => (
  <ul className="mt-5 space-y-2.5">
    {features.map((f, i) => (
      <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--gm-ink)" }}>
        {numbered ? (
          <span
            className="font-mono-gm mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-bold"
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.14)",
              color: "#fff",
            }}
          >
            {i + 1}
          </span>
        ) : (
          <Check
            className="mt-0.5 h-4 w-4 shrink-0"
            style={{ color: "var(--gm-success)" }}
            aria-hidden="true"
          />
        )}
        <span>{f}</span>
      </li>
    ))}
  </ul>
);

export const Pricing = ({ plans = [], config, availability, onEnquire }) => {
  const cards = plans.filter((p) => p.kind !== "source");
  const source = plans.find((p) => p.kind === "source");
  const deadline = availability?.is_open ? availability?.deadline : null;

  return (
    <section id="pricing" className="gm-section" style={{ background: "var(--gm-navy-950)" }}>
      <div className="gm-container" data-testid="pricing-section">
        <SectionHeader
          eyebrow="Pricing"
          title="Get Goldmap EA"
          subtitle="Pick the way you want to start. Free option is funded by our broker partnership, paid licences work with any broker."
          align="center"
          testId="pricing-header"
        />

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {cards.map((plan, i) => (
            <Reveal key={plan.slug} delay={i * 70}>
              <article
                data-testid={`pricing-card-${plan.slug}`}
                className="gm-card gm-card-hover relative flex h-full flex-col"
                style={
                  plan.highlight
                    ? {
                        borderColor: "rgba(224,31,38,0.5)",
                      }
                    : undefined
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <Badge plan={plan} />
                </div>

                <h3 className="mt-4 text-lg font-bold text-white">{plan.name}</h3>

                <div className="mt-3 flex items-end gap-1.5">
                  {plan.kind === "free" ? (
                    <span className="font-display text-3xl font-extrabold text-white">FREE</span>
                  ) : (
                    <>
                      <span className="font-display text-3xl font-extrabold text-white">
                        ${plan.price_label}
                      </span>
                      <span
                        className="pb-1 text-xs font-semibold"
                        style={{ color: "var(--gm-muted-2)" }}
                      >
                        {plan.currency}
                      </span>
                    </>
                  )}
                </div>
                <p className="mt-1.5 text-xs" style={{ color: "var(--gm-muted-2)" }}>
                  {plan.price_note}
                </p>

                <FeatureList features={plan.features} numbered={plan.kind === "free"} />

                {plan.kind === "free" && availability?.total ? (
                  <div className="mt-5">
                    <SlotsMeter
                      availability={availability}
                      testId="pricing-free-slots"
                      compact
                    />
                  </div>
                ) : null}

                {plan.has_countdown && deadline ? (
                  <div
                    className="mt-5 rounded-xl p-3"
                    style={{
                      background:
                        plan.kind === "free"
                          ? "rgba(224,31,38,0.08)"
                          : "rgba(255,255,255,0.04)",
                      border: `1px solid ${
                        plan.kind === "free"
                          ? "rgba(224,31,38,0.4)"
                          : "rgba(255,255,255,0.1)"
                      }`,
                    }}
                  >
                    <p className="text-[11px] font-semibold text-white">
                      {plan.urgency_label || "Offer ends in"}
                    </p>
                    <div className="mt-2">
                      <Countdown
                        target={deadline}
                        label={plan.name}
                        testId={`countdown-${plan.slug}`}
                      />
                    </div>
                    {plan.urgency_note ? (
                      <p
                        className="mt-2 text-[11px] leading-relaxed"
                        style={{ color: "var(--gm-muted-2)" }}
                      >
                        {plan.urgency_note}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-auto pt-6">
                  {plan.cta_type === "link" ? (
                    <Link
                      to={plan.cta_href || "/free-access"}
                      data-testid={`pricing-cta-${plan.slug}`}
                      className="gm-btn-ghost flex min-h-[48px] items-center justify-center gap-2 rounded-xl text-sm font-bold"
                    >
                      {plan.cta_label}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onEnquire(plan)}
                      data-testid={`pricing-cta-${plan.slug}`}
                      className="gm-btn-red flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold"
                    >
                      {plan.cta_label}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                  <p className="mt-3 text-[11px] leading-relaxed" style={{ color: "var(--gm-muted-2)" }}>
                    {plan.footnote}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {source ? (
          <Reveal delay={120}>
            <article
              data-testid={`pricing-card-${source.slug}`}
              className="gm-card mt-6 grid gap-6 lg:grid-cols-12 lg:items-center"
            >
              <div className="lg:col-span-7">
                <Badge plan={source} />
                <h3 className="mt-4 flex items-center gap-2 text-lg font-bold text-white">
                  <Code2 className="h-5 w-5" style={{ color: "#FF6B70" }} />
                  {source.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--gm-muted)" }}>
                  Full MQL4 and MQL5 source for Goldmap EA {config?.version || "v4.2.3"}. Read
                  every line, change the logic, plug it into your own tooling or rebrand
                  it for your own community.
                </p>
                <FeatureList features={source.features} />
              </div>

              <div className="lg:col-span-5">
                <div
                  className="rounded-xl p-5"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <div className="flex items-end gap-1.5">
                    <span className="font-display text-3xl font-extrabold text-white">
                      ${source.price_label}
                    </span>
                    <span
                      className="pb-1 text-xs font-semibold"
                      style={{ color: "var(--gm-muted-2)" }}
                    >
                      {source.currency}
                    </span>
                  </div>
                  <p className="mt-1 text-xs" style={{ color: "var(--gm-muted-2)" }}>
                    {source.price_note}
                  </p>

                  {deadline ? (
                    <div className="mt-4">
                      <p className="text-[11px] font-semibold text-white">
                        {source.urgency_label || "Current price held until"}
                        {source.next_price ? `, then ${source.next_price}` : ""}
                      </p>
                      <div className="mt-2">
                        <Countdown
                          target={deadline}
                          label="Source code price"
                          testId="countdown-source-code"
                        />
                      </div>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => onEnquire(source)}
                    data-testid="pricing-cta-source-code"
                    className="gm-btn-red mt-5 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold"
                  >
                    {source.cta_label}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <a
                    href={config?.telegram_owner || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="source-code-telegram-link"
                    className="gm-btn-ghost mt-2 flex min-h-[44px] items-center justify-center gap-2 rounded-xl text-sm font-semibold"
                  >
                    <Send className="h-4 w-4" />
                    Discuss on Telegram
                  </a>
                  <p className="mt-3 text-[11px]" style={{ color: "var(--gm-muted-2)" }}>
                    {source.footnote}
                  </p>
                </div>
              </div>
            </article>
          </Reveal>
        ) : null}

        <p
          className="mx-auto mt-8 max-w-2xl text-center text-xs leading-relaxed"
          data-testid="pricing-enquiry-note"
          style={{ color: "var(--gm-muted-2)" }}
        >
          Heads up: this site does not take payments. Every paid option is an enquiry -
          we reply on Telegram with the payment details and licence steps, then you
          decide.
        </p>
      </div>
    </section>
  );
};
